import type { TServerInfo } from '../shared';

export type THealthState = 'up' | 'down' | 'unknown';

export interface IDomainHealth {
  state: THealthState;
  /** Short badge text, e.g. "Up" or "Down for 3d 4h". */
  label: string;
  /** Secondary line, e.g. "Deletes in 19h" or the last time it responded. */
  detail?: string;
  /** Down and inside the deletion window, so the card should stand out. */
  isAtRisk: boolean;
}

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

// Coarse on purpose: "3d 4h" reads faster than "3d 4h 17m", and the sweep only
// refreshes hourly so finer precision would be false confidence.
export const formatDuration = (milliseconds: number): string => {
  if (milliseconds < MS_PER_MINUTE) return 'less than a minute';

  const days = Math.floor(milliseconds / MS_PER_DAY);
  const hours = Math.floor((milliseconds % MS_PER_DAY) / MS_PER_HOUR);
  const minutes = Math.floor((milliseconds % MS_PER_HOUR) / MS_PER_MINUTE);

  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${minutes}m`;
};

interface IGetDomainHealthInput {
  lastSeenUpAt?: string;
  graceDays: number;
  intervalMinutes: number;
  now?: number;
}

export const getDomainHealth = ({
  lastSeenUpAt,
  graceDays,
  intervalMinutes,
  now = Date.now()
}: IGetDomainHealthInput): IDomainHealth => {
  if (!lastSeenUpAt) {
    return {
      state: 'unknown',
      label: 'Never responded on /healthz',
      isAtRisk: false
    };
  }

  const lastSeenUpMs = Date.parse(lastSeenUpAt);
  if (Number.isNaN(lastSeenUpMs)) {
    return { state: 'unknown', label: 'Unknown', isAtRisk: false };
  }

  const elapsedMs = now - lastSeenUpMs;

  // Two sweeps of slack: a reading is only stale once a scheduled check could
  // have refreshed it and did not, which avoids flapping to "down" in the gap
  // between sweeps.
  if (elapsedMs < intervalMinutes * MS_PER_MINUTE * 2) {
    return { state: 'up', label: 'Up', isAtRisk: false };
  }

  const remainingMs = graceDays * MS_PER_DAY - elapsedMs;

  return {
    state: 'down',
    label: `Down for ${formatDuration(elapsedMs)}`,
    detail:
      remainingMs > 0
        ? `Deletes in ${formatDuration(remainingMs)}`
        : 'Past the grace period — deleted on the next sweep',
    isAtRisk: true
  };
};

export const getServerLabel = (serverInfo?: TServerInfo): string => {
  if (!serverInfo) return '';

  const parts = [
    serverInfo.name?.trim(),
    serverInfo.version ? `v${serverInfo.version}` : undefined
  ].filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join(' ') : '';
};
